// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState } from "react";
import {
    StyleSheet,
    View,
    Text,
    TouchableOpacity,
    TextInput,
    Animated,
    Easing,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import colors from "../../../config/colors";
import {
    deselectTransferGroup,
    createNoteTransferGroup,
    suspendTransferGroup,
} from "../../../axios/axiosConfig";
import { useNavigation } from "@react-navigation/native";
import fonts from "../../../config/fonts";
// Snackbar Component
const Snackbar = ({ message, visible, onHide, type }) => {
    const animation = React.useRef(new Animated.Value(0)).current;

    React.useEffect(() => {
        if (visible) {
            Animated.timing(animation, {
                toValue: 1,
                duration: 300,
                easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
            }).start(() => {
                setTimeout(() => {
                    Animated.timing(animation, {
                        toValue: 0,
                        duration: 300,
                        easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
                    }).start(onHide);
                }, 3000);
            });
        }
    }, [visible, animation, onHide]);

    const translateY = animation.interpolate({
        inputRange: [0, 1],
        outputRange: [100, 0],
    });

    return (
        <Animated.View
            style={[
                styles.snackbar,
                {
                    backgroundColor: type === "success" ? colors.green : colors.red,
                    transform: [{ translateY }],
                },
            ]}
        >
            <Text style={styles.snackbarText}>{message}</Text>
        </Animated.View>
    );
};

const ActiveTransport = ({
    getTranslation,
    handleCollapsedHeader,
    isCollapsed,
    activeTransportData,
}) => {
    const navigation = useNavigation();
    const [isNoteInputVisible, setIsNoteInputVisible] = useState(false);
    const [note, setNote] = useState("");
    const [emergencyNote, setEmergencyNote] = useState("");
    const [isEmergencyInputVisible, setIsEmergencyInputVisible] = useState(false);
    const [snackbar, setSnackbar] = useState({
        message: "",
        visible: false,
        type: "success",
    });

    const showSnackbar = (message, type = "success") => {
        setSnackbar({ message, visible: true, type });
    };

    const handleUndoClick = async () => {
        const response = await deselectTransferGroup(activeTransportData[0].id);
        if (response.status === 200) {
            showSnackbar(getTranslation("Richiesta sospesa con successo"));
            navigation.navigate(getTranslation("Richieste"));
        } else {
            showSnackbar(getTranslation(response.response.data.error), "error");
        }
    };

    const handleSaveNote = async () => {
        const response = await createNoteTransferGroup(
            activeTransportData[0].id,
            note
        );
        if (response.status === 200) {
            setIsNoteInputVisible(false);
            showSnackbar(getTranslation("Nota salvata con successo"));
        } else {
            showSnackbar(getTranslation(response.response.data.error), "error");
        }
    };

    const handleSaveEmergency = async () => {
        const response = await suspendTransferGroup(
            activeTransportData[0].id,
            emergencyNote
        );
        if (response.status === 200) {
            setIsEmergencyInputVisible(false);
            showSnackbar(
                getTranslation("Blocco di emergenza registrato con successo")
            );
            navigation.navigate(getTranslation("Richieste"));
        } else {
            showSnackbar(getTranslation(response.response.data.error), "error");
        }
    };
    const handleCall = () => {
        if (activeTransportData[0]?.phone) {
            window.open(`tel:${activeTransportData[0].phone}`);
        }
    };

    const toggleNoteInput = () => {
        setIsNoteInputVisible((prev) => !prev);
        if (isEmergencyInputVisible) setIsEmergencyInputVisible(false);
    };

    const toggleEmergencyInput = () => {
        setIsEmergencyInputVisible((prev) => !prev);
        if (isNoteInputVisible) setIsNoteInputVisible(false);
    };

    return (
        <View style={styles.container}>
            {activeTransportData[0]?.depositName ? (
                <TouchableOpacity
                    style={styles.header}
                    onPress={() => handleCollapsedHeader(!isCollapsed)}
                >
                    <Text style={styles.title}>{activeTransportData[0].depositName}</Text>
                    <View style={styles.badge}>
                        <Text style={styles.badgeText}>
                            {activeTransportData[0].transferNumber}
                        </Text>
                    </View>
                </TouchableOpacity>
            ) : (
                <View style={styles.emptyState}>
                    <Text style={styles.emptyStateText}>
                        {getTranslation(
                            "Nessun trasporto selezionato. Per favore, scegli una richiesta."
                        )}
                    </Text>
                </View>
            )}

            {!isCollapsed && activeTransportData[0]?.depositName && (
                <View style={styles.card}>
                    <View style={styles.buttonContainer}>
                        <TouchableOpacity
                            style={[styles.commonButton, styles.callButton]}
                            onPress={handleCall}
                        >
                            <MaterialIcons name="call" size={24} color={colors.green} />
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.commonButton, styles.emergencyButton]}
                            onPress={toggleEmergencyInput}
                        >
                            <MaterialIcons name="error" size={24} color="white" />
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.commonButton, styles.noteButton]}
                            onPress={toggleNoteInput}
                        >
                            <MaterialIcons name="note-add" size={24} color="white" />
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.commonButton, styles.undoButton]}
                            onPress={handleUndoClick}
                        >
                            <MaterialIcons name="arrow-back" size={24} color={colors.green} />
                        </TouchableOpacity>
                    </View>
                    {isNoteInputVisible && (
                        <View style={styles.noteInputContainer}>
                            <TextInput
                                style={styles.noteInput}
                                placeholder={getTranslation("Inserisci qui la tua nota...")}
                                value={note}
                                onChangeText={setNote}
                            />
                            <TouchableOpacity
                                style={styles.saveButton}
                                onPress={handleSaveNote}
                            >
                                <Text style={styles.labelButton}>
                                    {getTranslation("Conferma")}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}
                    {isEmergencyInputVisible && (
                        <View style={styles.emergencyInputContainer}>
                            <TextInput
                                style={styles.noteInput}
                                placeholder={getTranslation("Inserisci qui la tua nota...")}
                                value={emergencyNote}
                                onChangeText={setEmergencyNote}
                            />
                            <TouchableOpacity
                                style={styles.saveButton}
                                onPress={handleSaveEmergency}
                            >
                                <Text style={styles.labelButton}>
                                    {getTranslation("Conferma")}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
            )}
            {/* Snackbar Component */}
            <Snackbar
                message={snackbar.message}
                visible={snackbar.visible}
                onHide={() => setSnackbar((prev) => ({ ...prev, visible: false }))}
                type={snackbar.type}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.green,
        padding: 10,
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: 15,
        borderRadius: 20,
        backgroundColor: colors.white,
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        elevation: 8,
        marginVertical: 10,
    },
    emptyState: {
        alignItems: "center",
        justifyContent: "center",
        marginTop: 50,
    },
    emptyStateText: {
        fontSize: 18,
        color: colors.white,
        textAlign: "center",
        fontFamily: fonts.regular,
    },
    title: {
        fontSize: 22,
        fontWeight: "bold",
        color: colors.green,
        fontFamily: fonts.regular,
    },
    badge: {
        backgroundColor: colors.green,
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 5,
        fontFamily: fonts.regular
    },
    badgeText: {
        color: colors.white,
        fontWeight: "bold",
        fontFamily: fonts.regular
    },
    card: {
        width: "100%",
        padding: 20,
        borderRadius: 20,
        backgroundColor: colors.white,
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        elevation: 8,
        alignItems: "center",
        marginVertical: 10,
        fontFamily: fonts.regular
    },
    buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
    },
    commonButton: {
        flex: 1,
        padding: 10,
        borderRadius: 12,
        alignItems: "center",
        fontFamily: fonts.regular,
        marginHorizontal: 5,
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
        elevation: 5,
    },
    callButton: {
        backgroundColor: colors.white,
        borderColor: colors.green,
        borderWidth: 2,
    },
    emergencyButton: {
        backgroundColor: "red",
    },
    noteButton: {
        backgroundColor: "orange",
    },
    undoButton: {
        backgroundColor: colors.white,
        borderColor: colors.green,
        borderWidth: 2,
    },
    noteInputContainer: {
        marginTop: 20,
        width: "100%",
        alignItems: "center",
    },
    emergencyInputContainer: {
        marginTop: 20,
        width: "100%",
        alignItems: "center",
    },
    noteInput: {
        width: "100%",
        padding: 15,
        borderRadius: 12,
        borderColor: colors.green,
        borderWidth: 1,
        backgroundColor: colors.lightGray,
        marginBottom: 10,
    },
    saveButton: {
        width: "100%",
        backgroundColor: colors.green,
        borderRadius: 12,
        paddingVertical: 10,
        paddingHorizontal: 20,
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
        elevation: 5,
        marginTop: 10,
        fontFamily: fonts.regular,
    },
    labelButton: {
        color: colors.white,
        fontWeight: "bold",
        textAlign: "center",
        fontSize: 16,
        fontFamily: fonts.regular,
    },
    snackbar: {
        position: "absolute",
        bottom: 20,
        left: 20,
        right: 20,
        padding: 15,
        borderRadius: 12,
        elevation: 5,
    },
    snackbarText: {
        color: colors.white,
        fontWeight: "bold",
        textAlign: "center",
        fontFamily: fonts.regular
    },
});

export default ActiveTransport;
